/**
 * API Service — Barrel file providing backward-compatible ApiService class and re-exports.
 *
 * Implementation has been split into domain-specific modules:
 *   - apiClient.ts   — shared Axios instance, URL resolution, utilities
 *   - types.ts       — all TypeScript interfaces/types
 *   - batchApi.ts    — batch CRUD, upload, profiling, semantic summaries
 *   - mappingApi.ts  — mapping suggestions, recipes, feedback, approvals
 *   - schemaApi.ts   — schema sets, schema drafts, promotion, enrichment
 *   - connectorApi.ts— connector CRUD, discovery, testing, export
 *   - canvasApi.ts   — canvas workbooks, graph operations, events
 *   - projectApi.ts  — project CRUD, summaries
 *
 * All imports from '../services/api' continue to work unchanged.
 */

// Re-export all types so existing `import { SomeType } from '../services/api'` works
export * from './types';

// Re-export domain functions for direct use
export * from './batchApi';
export * from './mappingApi';
export * from './schemaApi';
export * from './connectorApi';
export * from './canvasApi';
export * from './projectApi';
export * from './notificationApi';
export * from './adminApi';
export * from './workspaceApi';

// Import domain modules for the ApiService wrapper class
import * as batchApi from './batchApi';
import * as mappingApi from './mappingApi';
import * as schemaApi from './schemaApi';
import * as connectorApi from './connectorApi';
import * as canvasApi from './canvasApi';
import * as projectApi from './projectApi';
import * as adminApi from './adminApi';

import type {
  Batch,
  Profile,
  MappingResponse,
  FieldMapping,
  ApplyTransformationRuleRequest,
  UpdateMappingRequest,
  CreateMappingRequest,
  TransformationTestResponse,
  ValidatorRunListResponse,
  SchemaDriftEventsResponse,
  MappingHealthResponse,
  TransformationRuleListResponse,
  ExpressionValidationResponse,
  RegenerationContextResponse,
  PromptRegenerateMappingRequest,
  Schema,
  SchemaListParams,
  SchemaCreateRequest,
  SchemaUpdateRequest,
  SchemaCloneRequest,
  SchemaPromoteRequest,
  SchemaGenerateRequest,
  SchemaGenerateResponse,
  SchemaUploadRequest,
  SchemaUploadResponse,
  ConnectorSchemaExtractRequest,
  ConnectorSchemaExtractResponse,
  ConnectorConfig,
  ExportToConnectorResponse,
  EdgeRegenerateRequest,
  EdgeRegenerateResponseModel,
  CanvasGraphModel,
  CanvasEventModel,
  RecordCanvasEventRequest,
  CreateManualAdjustmentRequest,
  ManualAdjustmentActionRequest,
  PromptLogEntry,
  Project,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectListParams,
  ProjectSummary,
  ProjectSummaryListResponse,
  Tenant,
  TenantCreateRequest,
  TenantUpdateRequest,
  UserAdminView,
  UserCreateRequest,
  UserUpdateRequest,
  FeatureFlag,
  FeatureFlagCreateRequest,
  FeatureFlagUpdateRequest,
} from './types';

/**
 * Backward-compatible ApiService class that delegates to domain modules.
 * Existing code using `apiService.someMethod()` continues to work.
 */
class ApiService {
  // --- Batch ---
  getBatches = batchApi.getBatches;
  getBatch = batchApi.getBatch;
  uploadFiles = batchApi.uploadFiles;
  getProfile = batchApi.getProfile;
  triggerProfiling = batchApi.triggerProfiling;
  getSemanticSummaries = batchApi.getSemanticSummaries;
  updateSemanticSummary = batchApi.updateSemanticSummary;
  chat = batchApi.chat;
  getSourcePreview = batchApi.getSourcePreview;
  getPromptLogs = batchApi.getPromptLogs;
  triggerBatchMapping = batchApi.triggerBatchMapping;
  getTransformationStatus = batchApi.getTransformationStatus;
  uploadBatch = batchApi.uploadBatch;
  createBatchFromConnector = batchApi.createBatchFromConnector;
  getStats = batchApi.getStats;
  downloadMappingGuide = batchApi.downloadMappingGuide;
  uploadMappingGuide = batchApi.uploadMappingGuide;
  previewMappingGuide = batchApi.previewMappingGuide;
  applyMappingGuideChanges = batchApi.applyMappingGuideChanges;

  // --- Mapping ---
  getMappings = mappingApi.getMappings;
  generateMappings = mappingApi.generateMappings;
  approveMappings = mappingApi.approveMappings;
  applyTransformationRule = mappingApi.applyTransformationRule;
  updateMapping = mappingApi.updateMapping;
  createMapping = mappingApi.createMapping;
  deleteMapping = mappingApi.deleteMapping;
  approveMapping = mappingApi.approveMapping;
  approveTable = mappingApi.approveTable;
  promptRegenerateMapping = mappingApi.promptRegenerateMapping;
  getRegenerationContext = mappingApi.getRegenerationContext;
  getTransformPreview = mappingApi.getTransformPreview;
  getDistributedPreview = mappingApi.getDistributedPreview;
  submitMappingFeedback = mappingApi.submitMappingFeedback;
  submitSuggestionFeedback = mappingApi.submitSuggestionFeedback;
  getValidatorTelemetry = mappingApi.getValidatorTelemetry;
  getSchemaDrift = mappingApi.getSchemaDrift;
  getMappingHealth = mappingApi.getMappingHealth;
  getPendingDecisions = mappingApi.getPendingDecisions;
  resolvePendingDecision = mappingApi.resolvePendingDecision;
  listTransformationRules = mappingApi.listTransformationRules;
  validateExpression = mappingApi.validateExpression;

  // --- Schema ---
  listSchemas = schemaApi.listSchemas;
  createSchema = schemaApi.createSchema;
  getSchema = schemaApi.getSchema;
  updateSchema = schemaApi.updateSchema;
  deleteSchema = schemaApi.deleteSchema;
  cloneSchema = schemaApi.cloneSchema;
  promoteSchema = schemaApi.promoteSchema;
  generateSchema = schemaApi.generateSchema;
  annotateSchema = schemaApi.annotateSchema;
  diffSchema = schemaApi.diffSchema;
  getSchemaEnrichmentLogs = schemaApi.getSchemaEnrichmentLogs;
  uploadSchema = schemaApi.uploadSchema;
  extractSchemaFromConnector = schemaApi.extractSchemaFromConnector;

  // --- Connector ---
  createConnector = connectorApi.createConnector;
  listConnectors = connectorApi.listConnectors;
  getConnectorCatalog = connectorApi.getConnectorCatalog;
  getConnector = connectorApi.getConnector;
  updateConnector = connectorApi.updateConnector;
  discoverStreams = connectorApi.discoverStreams;
  testConnector = connectorApi.testConnector;
  startTransformation = connectorApi.startTransformation;
  exportToConnector = connectorApi.exportToConnector;

  // --- Canvas ---
  listCanvasWorkbooks = canvasApi.listCanvasWorkbooks;
  getCanvasGraph = canvasApi.getCanvasGraph;
  saveCanvasGraph = canvasApi.saveCanvasGraph;
  listCanvasEvents = canvasApi.listCanvasEvents;
  recordCanvasEvent = canvasApi.recordCanvasEvent;
  createCanvasManualAdjustment = canvasApi.createCanvasManualAdjustment;
  undoCanvasManualAdjustment = canvasApi.undoCanvasManualAdjustment;
  redoCanvasManualAdjustment = canvasApi.redoCanvasManualAdjustment;
  regenerateCanvasEdge = canvasApi.regenerateCanvasEdge;
  runPreviewJob = canvasApi.runPreviewJob;
  getGeneratedSql = canvasApi.getGeneratedSql;

  // --- Project ---
  listProjects = projectApi.listProjects;
  getProjectSummary = projectApi.getProjectSummary;
  listProjectSummaries = projectApi.listProjectSummaries;
  getProject = projectApi.getProject;
  createProject = projectApi.createProject;
  updateProject = projectApi.updateProject;
  deleteProject = projectApi.deleteProject;
  softDeleteProject = projectApi.softDeleteProject;
  restoreProject = projectApi.restoreProject;

  // --- Admin ---
  getTenants = adminApi.getTenants;
  createTenant = adminApi.createTenant;
  updateTenant = adminApi.updateTenant;

  getUsers = adminApi.getUsers;
  createUser = adminApi.createUser;
  updateUser = adminApi.updateUser;
  resetUserMfa = adminApi.resetUserMfa;

  getInvitations = adminApi.getInvitations;
  sendInvitation = adminApi.sendInvitation;
  revokeInvitation = adminApi.revokeInvitation;
  resendInvitation = adminApi.resendInvitation;

  getFeatureFlags = adminApi.getFeatureFlags;
  createFeatureFlag = adminApi.createFeatureFlag;
  updateFeatureFlag = adminApi.updateFeatureFlag;
  deleteFeatureFlag = adminApi.deleteFeatureFlag;
}

export const apiService = new ApiService();
